import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { ScrollProgress } from '../shared/ScrollProgress';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'unset';
  }, [location]);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);
  const navLinks = [{
    path: '/',
    label: 'Home'
  }, {
    path: '/about',
    label: 'About'
  }, {
    path: '/services',
    label: 'Services'
  }, {
    path: '/portfolio',
    label: 'Portfolio'
  }, {
    path: '/equipment',
    label: 'Equipment'
  }, {
    path: '/csr',
    label: 'CSR'
  }, {
    path: '/contact',
    label: 'Contact'
  }];
  return <>
      <ScrollProgress data-id="element-139" />

      <motion.header initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'glass-effect shadow-soft border-b border-border' : 'bg-transparent'}`} data-id="element-140">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-141">
          <div className="flex items-center justify-between h-20" data-id="element-142">
            {/* Logo */}
            <Link to="/" className="flex items-center relative z-50" data-id="element-143">
              <motion.picture whileHover={{
              scale: 1.05
            }} transition={{
              duration: 0.3
            }} data-id="element-144">
                <source media="(min-width: 1024px)" srcSet="https://cdn.magicpatterns.com/uploads/k5fNtmwc2mFs1AhwSeu33E/FDF_Gold_Logo.png" data-id="element-145" />
                <source media="(min-width: 640px)" srcSet="https://cdn.magicpatterns.com/uploads/k5fNtmwc2mFs1AhwSeu33E/FDF_Gold_Logo.png" data-id="element-146" />
                <img src="https://cdn.magicpatterns.com/uploads/k5fNtmwc2mFs1AhwSeu33E/FDF_Gold_Logo.png" alt="FDF Gold Concept Nigeria Limited - Company Logo" className={`transition-all duration-300 object-contain ${isScrolled ? 'h-10 sm:h-12 lg:h-14 w-auto' : 'h-12 sm:h-14 lg:h-16 w-auto brightness-0 invert'}`} style={{
                filter: isScrolled ? 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))' : 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))',
                maxWidth: '200px'
              }} data-id="element-147" />
              </motion.picture>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-id="element-148">
              {navLinks.map(link => {
              const isActive = location.pathname === link.path;
              return <Link key={link.path} to={link.path} className="relative group" data-id="element-149">
                    <span className={`font-medium text-[15px] transition-colors duration-200 ${isScrolled ? isActive ? 'text-charcoal' : 'text-charcoal/70 hover:text-charcoal' : isActive ? 'text-white' : 'text-white/80 hover:text-white'}`} data-id="element-150">
                      {link.label}
                    </span>

                    {/* Active Indicator */}
                    {isActive && <motion.div layoutId="activeNav" className={`absolute -bottom-[21px] left-0 right-0 h-[2px] ${isScrolled ? 'bg-charcoal' : 'bg-white'}`} transition={{
                  type: 'spring',
                  stiffness: 350,
                  damping: 30
                }} data-id="element-151" />}
                  </Link>;
            })}
            </nav>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`lg:hidden relative z-50 p-2 transition-colors ${isScrolled ? 'text-charcoal' : 'text-white'} ${isMobileMenuOpen ? 'text-white' : ''}`} data-id="element-152">
              <AnimatePresence mode="wait" data-id="element-153">
                {isMobileMenuOpen ? <motion.div key="close" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.15
              }} data-id="element-154">
                    <X size={24} strokeWidth={2} data-id="element-155" />
                  </motion.div> : <motion.div key="menu" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.15
              }} data-id="element-156">
                    <Menu size={24} strokeWidth={2} data-id="element-157" />
                  </motion.div>}
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence data-id="element-158">
          {isMobileMenuOpen && <>
              {/* Backdrop */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="fixed inset-0 bg-charcoal/98 z-40" onClick={() => setIsMobileMenuOpen(false)} data-id="element-159" />

              {/* Menu Content */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="fixed inset-x-0 top-20 bottom-0 z-40 overflow-y-auto" data-id="element-160">
                <div className="min-h-full px-6 py-12" data-id="element-161">
                  <nav className="max-w-2xl mx-auto" data-id="element-162">
                    <div className="space-y-1" data-id="element-163">
                      {navLinks.map((link, index) => {
                    const isActive = location.pathname === link.path;
                    return <motion.div key={link.path} initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: index * 0.05,
                      duration: 0.3
                    }} data-id="element-164">
                            <Link to={link.path} className={`block py-4 px-6 font-heading font-semibold text-2xl transition-colors border-l-2 ${isActive ? 'text-white border-gold' : 'text-white/60 border-transparent hover:text-white hover:border-white/20'}`} data-id="element-165">
                              {link.label}
                            </Link>
                          </motion.div>;
                  })}
                    </div>
                  </nav>

                  {/* Contact Info */}
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.4,
                duration: 0.3
              }} className="max-w-2xl mx-auto mt-16 pt-8 border-t border-white/10 px-6" data-id="element-166">
                    <div className="space-y-2" data-id="element-167">
                      <p className="text-white/40 text-sm font-medium" data-id="element-168">
                        Contact
                      </p>
                      <a href="mailto:info@fdfgold.com" className="block text-white hover:text-gold transition-colors text-lg" data-id="element-169">
                        info@fdfgold.com
                      </a>
                      <p className="text-white/40 text-sm pt-4" data-id="element-170">
                        © {new Date().getFullYear()} FDF Gold Concept Nigeria
                        Limited
                      </p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </>}
        </AnimatePresence>
      </motion.header>
    </>;
}